import { useCallback } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useFormContext } from "react-hook-form";
import { useDispatchApp } from "../../../../lib/redux";
import { getPathDatabaseByRole } from "../../../../actions/auth";
import { generalFormatMediaUrl } from "../../../../services/adsCampaigns";
import { startDeleteAdMediaID } from "../../../../actions/adsCampaigns";
import IconButtonAddContent from "../../../Buttons/IconButtonAddContent";
import CardMedia from "../../../Media/CardMedia";
import {
  FORMATS_MEDIA_META,
  IMAGE_FORMATS,
  META,
  MIN_DIMENSIONS_CONTENT,
} from "../../../../utils/constants";
import { INPUT_COLOR_BORDER } from "../../../../utils/colors";

const MAX_CONTENTS = 10;

const ContentSelectorDynamicMeta = ({
  basicPath,
  adID,
  mode = "individual",
  disabled = false,
  language,
  onCallbackSave,
  propsSelectContentComponent = {},
}) => {
  const { watch, setValue } = useFormContext();
  const dispatch = useDispatchApp();
  const { userID } = dispatch(getPathDatabaseByRole());

  const mediaUrls = watch(`${basicPath}.mediaUrls`);
  const mediaData = watch(`${basicPath}.mediaData`);
  const mediaIDs = watch(`${basicPath}.mediaIDs`);

  const acceptedFormats =
    mode === "carousel" ? IMAGE_FORMATS : FORMATS_MEDIA_META;

  const canAddMore = mediaUrls.length < MAX_CONTENTS;

  const handleAddContent = useCallback(
    (contents, from = "upload") => {
      if (!contents?.length || !canAddMore) return;

      const newMediaUrls = [...mediaUrls];
      const newMediaData = [...mediaData];
      const newMediaIDs = [...(mediaIDs || [])];

      const toAdd = Math.min(contents.length, MAX_CONTENTS - mediaUrls.length);
      for (let i = 0; i < toAdd; i++) {
        const rawContent = contents[i];
        const content =
          typeof rawContent === "string"
            ? { url: rawContent, from: "upload" }
            : rawContent;
        const contentFrom = content?.from || from;
        const formatted = generalFormatMediaUrl({
          from: contentFrom,
          content,
        });
        if (formatted?.url) {
          newMediaUrls.push(formatted.url);
          newMediaData.push(formatted.mediaData || { from: "upload" });
          newMediaIDs.push("");
        }
      }

      setValue(`${basicPath}.mediaUrls`, newMediaUrls, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue(`${basicPath}.mediaData`, newMediaData, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue(`${basicPath}.mediaIDs`, newMediaIDs, {
        shouldValidate: true,
        shouldDirty: true,
      });
      onCallbackSave?.();
    },
    [
      mediaUrls,
      mediaData,
      mediaIDs,
      canAddMore,
      basicPath,
      setValue,
      onCallbackSave,
    ]
  );

  const handleRemoveContent = useCallback(
    async (index) => {
      if (mediaIDs?.[index]) {
        await dispatch(
          startDeleteAdMediaID({
            adID,
            index,
          })
        );
      }

      const newMediaUrls = mediaUrls.filter((_, i) => i !== index);
      const newMediaData = mediaData.filter((_, i) => i !== index);
      const newMediaIDs = (mediaIDs || []).filter((_, i) => i !== index);

      setValue(`${basicPath}.mediaUrls`, newMediaUrls, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue(`${basicPath}.mediaData`, newMediaData, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue(`${basicPath}.mediaIDs`, newMediaIDs, {
        shouldValidate: true,
        shouldDirty: true,
      });
      onCallbackSave?.();
    },
    [
      mediaUrls,
      mediaData,
      mediaIDs,
      adID,
      basicPath,
      setValue,
      dispatch,
      onCallbackSave,
    ]
  );

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          alignItems: "flex-start",
        }}
      >
        {mediaUrls.map((url, index) => (
          <Box
            key={index}
            sx={{
              position: "relative",
              width: 120,
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                border: `1px solid ${INPUT_COLOR_BORDER}`,
                borderRadius: 2,
                overflow: "hidden",
                position: "relative",
              }}
            >
              <CardMedia
                url={url}
                sx={{
                  objectFit: "cover",
                  borderRadius: 2,
                  height: 120,
                  width: "100%",
                }}
                propsType={{
                  controls: false,
                  autoplay: true,
                  muted: true,
                  loop: true,
                }}
              />
              {!disabled && (
                <IconButton
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    backgroundColor: "white !important",
                    "&:hover": {
                      backgroundColor: "white !important",
                    },
                  }}
                  onClick={() => handleRemoveContent(index)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Box>
        ))}
        {!disabled && canAddMore && (
          <Box
            sx={{
              width: 120,
              height: 120,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <IconButtonAddContent
              id={`content-selector-dynamic-${basicPath}`}
              mode="icon"
              multiple={true}
              pathStorage={`adsCampaigns/${userID}/`}
              modeUpload="sync"
              acceptedFormats={acceptedFormats}
              minDimensions={MIN_DIMENSIONS_CONTENT}
              initialValues={{ language, platform: META }}
              onAddContent={(contents) => {
                handleAddContent(contents, "upload");
              }}
              onCallbackSaveCanva={(mediaUrl) => {
                handleAddContent([{ url: mediaUrl, from: "upload" }]);
              }}
              icon={
                <AddIcon
                  sx={{ color: "#000 !important", height: 40, width: 40 }}
                />
              }
              disabled={disabled}
              sx={{
                width: "100%",
                height: "100%",
              }}
              {...propsSelectContentComponent}
            />
          </Box>
        )}
      </Box>
      {mediaUrls.length > 0 && (
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{ mt: 1, display: "block" }}
        >
          {mediaUrls.length} / {MAX_CONTENTS}
        </Typography>
      )}
    </Box>
  );
};

export default ContentSelectorDynamicMeta;
